import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  ClipboardList,
  CheckCircle2,
  XCircle,
  Trash2,
  FileText,
  AlertCircle,
  Plane,
  Upload,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { uploadFile, fileUrl } from "../lib/files";

const statusMap = {
  pending:  { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Aprobada",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rechazada", cls: "bg-red-50 text-red-700 border-red-200" },
};

const groupColor = {
  ausencia:    "bg-red-50 text-red-700 border-red-200",
  incapacidad: "bg-blue-50 text-blue-700 border-blue-200",
  vacaciones:  "bg-violet-50 text-violet-700 border-violet-200",
  permiso:     "bg-zinc-100 text-zinc-700 border-zinc-200",
};

const emptyForm = {
  branch_id: "",
  employee_id: "",
  type: "",
  start_date: "",
  end_date: "",
  paid: null,
  notes: "",
  doctor_name: "",
  folio: "",
  attachment_url: "",
};

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [types, setTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [savingIncident, setSavingIncident] = useState(false);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);

  const isIncapacidad = form.type === "incapacidad_ccss" || form.type === "incapacidad_ins";

  const handleComprobanteFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingComprobante(true);
    try {
      const rec = await uploadFile(file, { category: "incident", entityId: form.employee_id || "incident" });
      setForm((f) => ({ ...f, attachment_url: fileUrl(rec.storage_path) }));
      toast.success("Comprobante adjuntado");
    } catch {
      toast.error("No se pudo subir el comprobante");
    } finally {
      setUploadingComprobante(false);
      e.target.value = "";
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterBranch !== "all") params.branch_id = filterBranch;
      if (filterType !== "all") params.type = filterType;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const [incRes, empRes, brRes, typRes, sumRes] = await Promise.all([
        api.get("/incidents", { params }),
        api.get("/employees", { params: { limit: 1000 } }),
        api.get("/branches"),
        api.get("/incidents/types"),
        api.get("/incidents/summary"),
      ]);
      setItems(incRes.data);
      setEmployees(empRes.data.items || []);
      setBranches(brRes.data);
      setTypes(typRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error("Error al cargar incidencias");
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterType, filterStatus, filterFrom, filterTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = items.filter((i) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (i.employee_name || "").toLowerCase().includes(s) ||
      (i.employee_code || "").toLowerCase().includes(s) ||
      (i.type_label || "").toLowerCase().includes(s) ||
      (i.folio || "").toLowerCase().includes(s)
    );
  });

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (inc) => {
    setForm({
      branch_id: inc.branch_id || "",
      employee_id: inc.employee_id,
      type: inc.type,
      start_date: inc.start_date,
      end_date: inc.end_date,
      paid: inc.paid,
      notes: inc.notes || "",
      doctor_name: inc.doctor_name || "",
      folio: inc.folio || "",
      attachment_url: inc.attachment_url || "",
    });
    setEditingId(inc.id);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (savingIncident) return; // evita doble registro por doble clic
    if (!form.employee_id || !form.type || !form.start_date || !form.end_date) {
      toast.error("Sucursal, empleado, tipo y fechas son requeridos");
      return;
    }
    setSavingIncident(true);
    try {
      const payload = { ...form };
      delete payload.branch_id; // backend deriva branch_id del empleado
      if (payload.paid === null) delete payload.paid;
      if (editingId) {
        await api.patch(`/incidents/${editingId}`, payload);
        toast.success("Incidencia actualizada");
      } else {
        await api.post("/incidents", payload);
        toast.success("Incidencia registrada");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar incidencia");
    } finally {
      setSavingIncident(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/incidents/${id}/approve`, { approved_by: "RRHH" });
      toast.success("Incidencia aprobada");
      fetchData();
    } catch {
      toast.error("Error al aprobar");
    }
  };

  const handleReject = async (id) => {
    const notes = window.prompt("Motivo del rechazo (opcional):", "") || "";
    try {
      await api.post(`/incidents/${id}/reject`, { approved_by: "RRHH", notes });
      toast.success("Incidencia rechazada");
      fetchData();
    } catch {
      toast.error("Error al rechazar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta incidencia?")) return;
    try {
      await api.delete(`/incidents/${id}`);
      toast.success("Incidencia eliminada");
      fetchData();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const downloadReceipt = async (inc) => {
    try {
      const res = await api.get(`/incidents/${inc.id}/vacation-receipt`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el comprobante");
    }
  };

  const selectedType = types.find((t) => t.value === form.type);
  const computedIncidentDays = (() => {
    if (!form.start_date) return 0;
    const s = new Date(form.start_date + "T00:00:00");
    const e = new Date((form.end_date || form.start_date) + "T00:00:00");
    if (isNaN(s) || isNaN(e) || e < s) return 0;
    return Math.round((e - s) / 86400000) + 1; // días naturales inclusivos
  })();

  return (
    <div data-testid="incidents-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Incidencias
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Ausencias, incapacidades, vacaciones y permisos
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-incident-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva incidencia
        </Button>
      </div>

      {/* Summary mini-cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="incidents-summary">
          <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Total del mes</p>
              <p className="text-2xl font-bold text-zinc-950 mt-1">{summary.total_incidents}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Días acumulados</p>
              <p className="text-2xl font-bold text-zinc-950 mt-1">{summary.total_days}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-500">Pendientes</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{summary.by_status?.pending || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-500">Aprobadas</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{summary.by_status?.approved || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            className="pl-9"
            placeholder="Buscar por empleado, tipo o folio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="incident-search-input"
          />
        </div>
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[180px]" data-testid="incident-filter-branch">
            <SelectValue placeholder="Sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[200px]" data-testid="incident-filter-type">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {types.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="incident-filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="approved">Aprobada</SelectItem>
            <SelectItem value="rejected">Rechazada</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={filterFrom}
          onChange={(e) => setFilterFrom(e.target.value)}
          className="w-[150px]"
          data-testid="incident-filter-from"
        />
        <Input
          type="date"
          value={filterTo}
          onChange={(e) => setFilterTo(e.target.value)}
          className="w-[150px]"
          data-testid="incident-filter-to"
        />
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Empleado</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Tipo</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Inicio</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Fin</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Días</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Pago</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Estado</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inc) => (
                <TableRow
                  key={inc.id}
                  className="border-zinc-100 hover:bg-zinc-50/80 transition-colors"
                  data-testid={`incident-row-${inc.id}`}
                >
                  <TableCell>
                    <div>
                      <p className="text-sm text-zinc-900 font-medium">{inc.employee_name || "\u2014"}</p>
                      <p className="text-[11px] text-zinc-400 font-mono">{inc.employee_code}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`font-medium ${groupColor[inc.type_group] || ""}`}>
                      {inc.type_label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">{inc.start_date}</TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">{inc.end_date}</TableCell>
                  <TableCell className="text-sm text-zinc-900 font-semibold text-right">{inc.days}</TableCell>
                  <TableCell>
                    {inc.paid ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Con goce</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200">Sin goce</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`font-medium ${statusMap[inc.status]?.cls || ""}`}>
                      {statusMap[inc.status]?.label || inc.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {inc.status === "pending" && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                            onClick={() => handleApprove(inc.id)}
                            title="Aprobar"
                            data-testid={`approve-incident-${inc.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleReject(inc.id)}
                            title="Rechazar"
                            data-testid={`reject-incident-${inc.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(inc)}
                        title="Editar"
                        data-testid={`edit-incident-${inc.id}`}
                      >
                        <FileText className="w-4 h-4 text-zinc-400" />
                      </Button>
                      {inc.type === "vacaciones" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                          onClick={() => downloadReceipt(inc)}
                          title={`Comprobante de vacaciones${inc.receipt_number ? " · " + inc.receipt_number : ""}`}
                          data-testid={`vacation-receipt-${inc.id}`}
                        >
                          <Plane className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDelete(inc.id)}
                        title="Eliminar"
                        data-testid={`delete-incident-${inc.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16 text-zinc-400">
                    <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay incidencias registradas</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editingId ? "Editar incidencia" : "Nueva incidencia"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Registra ausencias, incapacidades, vacaciones o permisos. Los días se calculan automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">Sucursal *</Label>
              <Select
                value={form.branch_id}
                onValueChange={(v) => setForm({ ...form, branch_id: v, employee_id: "" })}
                disabled={!!editingId}
              >
                <SelectTrigger data-testid="incident-branch-select">
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Empleado *</Label>
              <Select
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                disabled={!!editingId || !form.branch_id}
              >
                <SelectTrigger data-testid="incident-employee-select">
                  <SelectValue placeholder={form.branch_id ? "Seleccionar empleado" : "Primero elige una sucursal"} />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    .filter((e) => !form.branch_id || e.primary_branch_id === form.branch_id)
                    .map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.full_name} · {e.employee_code}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {form.branch_id &&
                employees.filter((e) => e.primary_branch_id === form.branch_id).length === 0 && (
                  <p className="text-[11px] text-zinc-400 mt-1">
                    No hay empleados activos en esta sucursal
                  </p>
                )}
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">Tipo de incidencia *</Label>
              <Select
                value={form.type}
                onValueChange={(v) => {
                  const t = types.find((x) => x.value === v);
                  const incap = v === "incapacidad_ccss" || v === "incapacidad_ins";
                  setForm({
                    ...form,
                    type: v,
                    paid: t ? t.paid_default : null,
                    // Limpiar campos de comprobante si no es incapacidad
                    ...(incap ? {} : { folio: "", attachment_url: "", doctor_name: "" }),
                  });
                }}
              >
                <SelectTrigger data-testid="incident-type-select">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedType && (
                <p className="text-[11px] text-zinc-400 mt-1">
                  Por defecto: {selectedType.paid_default ? "con goce de salario" : "sin goce de salario"}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">Fecha inicio *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  max={form.end_date || undefined}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="incident-start-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Fecha fin *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  min={form.start_date || undefined}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="incident-end-input"
                />
              </div>
            </div>
            <div
              className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3"
              data-testid="incident-days-box"
            >
              <span className="text-[11px] uppercase tracking-wide font-medium text-emerald-700">Días calculados (naturales)</span>
              <span className="text-2xl font-extrabold text-emerald-700 tabular-nums" data-testid="incident-days-count">
                {computedIncidentDays > 0 ? `${computedIncidentDays}` : "—"}
                {computedIncidentDays > 0 && (
                  <span className="text-sm font-semibold ml-1">día{computedIncidentDays === 1 ? "" : "s"}</span>
                )}
              </span>
            </div>
            {isIncapacidad && (
              <div className="space-y-4 rounded-lg border border-blue-100 bg-blue-50/40 p-3" data-testid="incident-incapacidad-fields">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Datos de la incapacidad</p>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">ID comprobante</Label>
                  <Input
                    value={form.folio}
                    onChange={(e) => setForm({ ...form, folio: e.target.value })}
                    placeholder="Ej: 2026-00001"
                    data-testid="incident-folio-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Comprobante (foto, PDF o enlace)</Label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    id="incident-comprobante-file"
                    className="hidden"
                    onChange={handleComprobanteFile}
                  />
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      disabled={uploadingComprobante}
                      onClick={() => document.getElementById("incident-comprobante-file").click()}
                      data-testid="incident-comprobante-upload"
                    >
                      {uploadingComprobante ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Subir foto / PDF
                    </Button>
                    <span className="text-[11px] text-zinc-400">o pega un enlace abajo</span>
                  </div>
                  <Input
                    className="mt-2"
                    value={form.attachment_url}
                    onChange={(e) => setForm({ ...form, attachment_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="incident-attachment-input"
                  />
                  {form.attachment_url && (
                    <a
                      href={form.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-blue-600 hover:underline"
                      data-testid="incident-attachment-preview"
                    >
                      <ExternalLink className="w-3 h-3" /> Ver comprobante adjunto
                    </a>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-zinc-700">Notas</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="incident-notes-input"
              />
            </div>
            {form.type && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 border border-zinc-200">
                <AlertCircle className="w-4 h-4 text-zinc-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] text-zinc-600">Pago durante el período:</p>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={form.paid ? "default" : "outline"}
                      className={form.paid ? "bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" : "h-7 text-xs"}
                      onClick={() => setForm({ ...form, paid: true })}
                      data-testid="incident-paid-yes"
                    >
                      Con goce
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={!form.paid ? "default" : "outline"}
                      className={!form.paid ? "bg-zinc-900 hover:bg-zinc-800 text-white h-7 text-xs" : "h-7 text-xs"}
                      onClick={() => setForm({ ...form, paid: false })}
                      data-testid="incident-paid-no"
                    >
                      Sin goce
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="incident-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={savingIncident}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="incident-submit-button"
            >
              {savingIncident ? "Guardando…" : (editingId ? "Guardar cambios" : "Registrar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
